/* The counterparty card's Comments tab, stage 1: the team's text notes, newest first.

     GET    excalibur/comments/?counterparty=<id>        → {next, previous, results}
     POST   excalibur/comments/  {counterparty, text}     → 201, the row
     PATCH  excalibur/comments/:id/  {text}               → 200, the row (`modified` moves on)
     DELETE excalibur/comments/:id/                       → 204 (soft)

   A row is `{id, counterparty, text, author, created, modified, can_edit}` — every comment is a
   person's note; there are no system events. "Edited" is `modified` later than `created` (equal
   on create), compared as dates. `?counterparty=` goes on EVERY list request: without it the API
   answers the whole company's comments, not a 400. A counterparty outside the company (or gone)
   lists empty; on create it is refused under `counterparty`. Any key beyond these two is silently
   ignored (no 400), so nothing else is sent.

   Cursor pagination, 20 a page. `next` is an ABSOLUTE url and `call()` prepends API_URL_PREFIX, so
   it is cut back to the path first (the trap `/tracking` notes). Pages append to the end, rows
   already shown skipped by id — a note added on top, or a row the cursor hands over twice, never
   shows twice. "Load more" only: no loading on scroll. A failed "Load more" keeps what is shown.

   Days are grouped by the date of `created`, read as the browser's local time — the API sends it
   with no zone, in the SERVER's (Europe/Kyiv in production; the local dev API runs UTC, so there a
   new note reads three hours early). Today / Yesterday against the browser's today, other days
   DD.MM.YYYY as every date on the card; inside a day, HH:mm only.

   The new-note draft lives in `Page.js` (`draft` / `setDraft`), above the tab, so switching tabs
   keeps it. Ctrl+Enter / ⌘+Enter saves, both here and in an inline edit; plain Enter and
   Shift+Enter stay line breaks, and an IME composition is never cut short.

   Edit / Delete only where the API says `can_edit` — the author's own note, any role; no role check
   here. On a wide screen they
   show on hover and when focus is inside the note (`:focus-within`); on a narrow or touch screen
   always (`counterparties.scss`). */

import React, { useEffect, useRef, useState } from "react";
import { Alert as Notice, Avatar, Button, Empty, Flex, Input, Modal, Skeleton, Typography } from "antd";

import Alert from "/src/util/alert";

import { fullName, initialsOf } from "../../component/table-cells";
import { API_URL_PREFIX } from "../../config/config";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, parseErrors } from "../../util/action";
import dayjs from "../../util/dayjs";
import { translate } from "../../util/util";

const BASE = "excalibur/comments/";
const MAX = 4000;
const toast = { position: "top-right" };
const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
const SHORTCUT_KEY = isMac ? "⌘" : "Ctrl";

/** `next` as the path `call()` takes. */
const relative = next => {
  if (!next) return null;
  if (next.startsWith(API_URL_PREFIX)) return next.slice(API_URL_PREFIX.length);
  const at = next.indexOf("/web/v1/");
  return at >= 0 ? next.slice(at + "/web/v1/".length) : next;
};

/** Ctrl/⌘+Enter → `action`; everything else (Enter, Shift+Enter, an IME composition) untouched. */
const saveKey = action => event => {
  if (event.key === "Enter" && (event.ctrlKey || event.metaKey) && !event.nativeEvent.isComposing) {
    event.preventDefault();
    action();
  }
};

const merge = (list, more) => {
  const seen = new Set(list.map(c => c.id));
  return [...list, ...more.filter(c => !seen.has(c.id))];
};

/** Edited: `modified` later than `created` — as moments, not strings (both come with no zone). */
const isEdited = note => Boolean(note.created && note.modified) && dayjs(note.modified).isAfter(dayjs(note.created));

function dayLabel(created, t) {
  const day = dayjs(created).startOf("day");
  const today = dayjs().startOf("day");
  if (day.isSame(today)) return t("cm_today");
  if (day.isSame(today.subtract(1, "day"))) return t("cm_yesterday");
  return day.format("DD.MM.YYYY");
}

function Note({ note, t, onChanged, onRemoved, lang }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(note.text);
  const [busy, setBusy] = useState(false);
  const mayEdit = note.can_edit;
  const name = fullName(note.author) || t("cm_unknown_author");
  const time = note.created ? dayjs(note.created).format("HH:mm") : "";

  const save = () => {
    const next = text.trim();
    if (!next || next === note.text) return;
    setBusy(true);
    call({ method: "PATCH", url: `${BASE}${note.id}/`, post: { text: next } }, response => {
      setBusy(false);
      if (response?.id) {
        onChanged(response);
        setEditing(false);
        Alert.success(t("cm_updated_msg"), toast);
        return;
      }
      /* 403 / 404: `call()` hands a 404 `{detail}` over as data, a 403 logs out (handled there). */
      if (!response?.failed) Alert.warning(t(response?.detail ? "cm_forbidden_msg" : "edit_error_msg"), toast);
    });
  };
  const cancel = () => {
    setText(note.text);
    setEditing(false);
  };

  /* Destructive, so confirmed — the start of the note in the question, the verb on the button. */
  const remove = () =>
    Modal.confirm({
      title: t("cm_delete_title"),
      content: note.text.length > 80 ? `${note.text.slice(0, 80)}…` : note.text,
      okText: t("cm_delete"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `${BASE}${note.id}/` }, response => {
            resolve();
            if (response?.status === 204) {
              onRemoved(note.id);
              Alert.success(t("cm_deleted_msg"), toast);
            } else if (!response?.failed) Alert.warning(t(response?.detail ? "cm_forbidden_msg" : "edit_error_msg"), toast);
          })
        ),
    });

  return (
    <li className="cp-cm__item" data-id={note.id}>
      <Avatar size={28} className="lp-who__av" style={{ fontSize: 12 }} src={note.author?.photo || null}>
        {initialsOf(fullName(note.author)) || "?"}
      </Avatar>
      <div className="cp-cm__body">
        <Flex className="cp-cm__meta" align="center" gap={6} wrap>
          <Typography.Text strong className="cp-cm__author">
            {name}
          </Typography.Text>
          <span className="cp-cm__time lp-num">{time}</span>
          {isEdited(note) ? <span className="cp-cm__time">· {t("cm_edited")}</span> : null}
          {mayEdit && !editing ? (
            <span className="cp-cm__actions">
              {/* Every note has the same two buttons: the accessible name says whose note, and when. */}
              <Button type="link" size="small" aria-label={`${t("cm_edit")}: ${name}, ${time}`} onClick={() => setEditing(true)}>
                {t("cm_edit")}
              </Button>
              <Button type="link" size="small" danger aria-label={`${t("cm_delete")}: ${name}, ${time}`} onClick={remove}>
                {t("cm_delete")}
              </Button>
            </span>
          ) : null}
        </Flex>
        {editing ? (
          <Flex vertical gap={8} className="cp-cm__edit">
            <Input.TextArea
              autoFocus
              autoSize={{ minRows: 2, maxRows: 10 }}
              maxLength={MAX}
              showCount
              value={text}
              aria-label={t("cm_edit")}
              onChange={e => setText(e.target.value)}
              onKeyDown={event => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  cancel();
                  return;
                }
                saveKey(save)(event);
              }}
            />
            <Flex gap={8} justify="flex-end">
              <Button onClick={cancel}>{t("cm_cancel")}</Button>
              <Button type="primary" loading={busy} disabled={!text.trim() || text.trim() === note.text} onClick={save}>
                {t("cm_save")}
              </Button>
            </Flex>
          </Flex>
        ) : (
          <div className="cp-cm__text">{note.text}</div>
        )}
      </div>
    </li>
  );
}

export default function CommentsTab({ counterpartyId, lang, draft, setDraft }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const [items, setItems] = useState(null);
  const [next, setNext] = useState(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draftError, setDraftError] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    setError(false);
    setItems(null);
    /* Never without the counterparty: the API would answer every comment of the company. */
    if (!counterpartyId) return;
    call({ method: "GET", url: `${BASE}?counterparty=${counterpartyId}` }, response => {
      if (mine !== seq.current) return;
      if (!response || response.failed || !Array.isArray(response.results)) {
        setItems([]);
        setError(true);
        return;
      }
      setItems(response.results);
      setNext(relative(response.next));
    });
  }, [counterpartyId, reload]);

  const loadMore = () => {
    if (!next) return;
    setLoadingMore(true);
    setMoreError(false);
    call({ method: "GET", url: next }, response => {
      setLoadingMore(false);
      if (!response || response.failed || !Array.isArray(response.results)) {
        setMoreError(true);
        return;
      }
      setItems(prev => merge(prev || [], response.results));
      setNext(relative(response.next));
    });
  };

  const text = draft.trim();
  const create = () => {
    if (!text || busy) return;
    setBusy(true);
    setDraftError(false);
    call({ method: "POST", url: BASE, post: { counterparty: counterpartyId, text } }, response => {
      setBusy(false);
      if (response?.id) {
        setItems(prev => [response, ...(prev || []).filter(c => c.id !== response.id)]);
        setDraft("");
        Alert.success(t("cm_saved_msg"), toast);
        return;
      }
      if (typeof response?.json !== "function") return; // a 5xx / network failure — `call()` toasted it
      /* A 400 names its field: `text` is the draft's own (marked under it), `counterparty` means
         the card's counterparty is gone or not the company's — nothing the draft can fix. */
      parseErrors(response).then(parsed => {
        const keys = Object.keys(parsed.fields);
        if (keys.includes("text")) setDraftError(true);
        if (keys.includes("counterparty")) Alert.warning(t("card_not_found_title"), toast);
        else if (!keys.includes("text")) Alert.warning(t("edit_error_msg"), toast);
      });
    });
  };

  const changed = row => setItems(prev => prev.map(c => (c.id === row.id ? row : c)));
  const removed = id => setItems(prev => prev.filter(c => c.id !== id));

  /* Day groups over the loaded rows, in order. */
  const groups = [];
  (items || []).forEach(note => {
    const label = dayLabel(note.created, t);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.notes.push(note);
    else groups.push({ label, notes: [note] });
  });

  return (
    <section className="cp-cm" aria-label={t("tab_comments")}>
      <div className="cp-cm__composer">
        <Input.TextArea
          id="cp-cm-new"
          autoSize={{ minRows: 2, maxRows: 10 }}
          maxLength={MAX}
          showCount
          value={draft}
          placeholder={t("cm_placeholder")}
          aria-label={t("cm_placeholder")}
          aria-invalid={draftError || undefined}
          aria-describedby={draftError ? "cp-cm-new-error" : undefined}
          status={draftError ? "error" : undefined}
          onChange={e => {
            setDraft(e.target.value);
            setDraftError(false);
          }}
          onKeyDown={saveKey(create)}
        />
        <Flex className="cp-cm__composer-foot" align="center" justify="space-between" gap="small" wrap>
          <Typography.Text type="secondary" className="cp-cm__hint">
            {t("cm_hint")}
          </Typography.Text>
          <Flex align="center" gap="small" className="cp-cm__composer-actions">
            <span className="cp-cm__shortcut">{t("cm_shortcut").replace("{key}", SHORTCUT_KEY)}</span>
            <Button type="primary" loading={busy} disabled={!text} onClick={create}>
              {t("cm_save")}
            </Button>
          </Flex>
        </Flex>
        {draftError ? (
          <Typography.Text type="danger" className="cp-cm__hint" id="cp-cm-new-error">
            {t("ad_field_invalid")}
          </Typography.Text>
        ) : null}
      </div>

      {items === null ? (
        <Skeleton active avatar paragraph={{ rows: 3 }} />
      ) : error ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{t("tab_load_error")}</Typography.Text>
              <Button size="small" onClick={() => setReload(k => k + 1)}>
                {t("retry")}
              </Button>
            </Flex>
          }
        />
      ) : !items.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("cm_empty")} />
      ) : (
        <>
          {groups.map(group => (
            <div key={group.label} className="cp-cm__day">
              {/* Not `role="separator"`: that role hides its content, and the day is the point. The
                  rules either side are pseudo-elements. */}
              <div className="cp-cm__divider">
                <span>{group.label}</span>
              </div>
              <ul className="cp-cm__list">
                {group.notes.map(note => (
                  <Note key={note.id} note={note} t={t} lang={lang} onChanged={changed} onRemoved={removed} />
                ))}
              </ul>
            </div>
          ))}
          {moreError ? (
            <Notice
              type="error"
              showIcon
              className="cp-cm__more-error"
              title={t("cm_more_error")}
              action={
                <Button size="small" onClick={loadMore}>
                  {t("retry")}
                </Button>
              }
            />
          ) : null}
          {next && !moreError ? (
            <Flex justify="center" className="cp-cm__more">
              <Button loading={loadingMore} onClick={loadMore}>
                {t("cm_more")}
              </Button>
            </Flex>
          ) : null}
        </>
      )}
    </section>
  );
}
